// El selector de voz de Videos cortos (ADR-2026-10-30-videos-cortos-voces-del-estudio): las MISMAS
// opciones que el generador de voz en off del Estudio de Video —pestañas "Mis voces" (las
// clonadas de la empresa) y "Voces Atlas" (el catálogo entero, con los filtros Género e Idioma),
// cada voz con nombre, idioma · acento, sexo · descripción y el ▶ para escucharla— más dos
// opciones propias de este modo arriba: Automática (Atlas elige por producto según para quién
// es) y Sin voz (sólo música). Va en `HojaLateral`, la de Diseño y del Catálogo: se abre desde
// la configuración (la voz de la tanda) y desde cada tarjeta (la voz de ese producto). Elegir
// cierra, como en el Estudio.
import { Check, Loader2, Mic, Pause, Play, Sparkles, VolumeX } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HojaLateral } from '../../../../chat-atlas/ui/HojaLateral';
import { SelectorDeOpciones } from '../catalogoAgente/SelectorDeOpciones';
import type { VozDeVideo, VozDelEstudio } from './videosCortosApi';

const nombreCorto = (n: string): string => (n.includes(' - ') ? (n.split(' - ')[0] ?? n) : n || 'Sin nombre');
const descripcionLimpia = (d: string | null): string => {
  if (!d || d.includes('SILENT THOUGHT') || d.includes('\\n')) return '';
  return d.length > 100 ? `${d.slice(0, 100)}…` : d;
};

export interface SelectorDeVozProps {
  abierto: boolean;
  /** Para quién se elige ("la tanda", "Remera Oversize"). */
  para: string;
  voces: VozDelEstudio[];
  cargando: boolean;
  elegida: VozDeVideo | null;
  /** Qué significa "Automática" acá (la de la tanda, o según el producto). */
  bajadaDeAutomatica?: string;
  onElegir: (voz: VozDeVideo | null) => void;
  onCerrar: () => void;
}

export const SelectorDeVoz: React.FC<SelectorDeVozProps> = ({ abierto, para, voces, cargando, elegida, bajadaDeAutomatica, onElegir, onCerrar }) => {
  const clonadas = useMemo(() => voces.filter((v) => v.clonada), [voces]);
  const catalogo = useMemo(() => voces.filter((v) => !v.clonada), [voces]);
  const [pestana, setPestana] = useState<'mis-voces' | 'voces-atlas'>('voces-atlas');
  const [genero, setGenero] = useState('Todas');
  const [idioma, setIdioma] = useState('Todas');
  const generos = useMemo(() => ['Todas', ...Array.from(new Set(catalogo.map((v) => v.sexo).filter((x): x is string => !!x))).sort()], [catalogo]);
  const idiomas = useMemo(() => ['Todas', ...Array.from(new Set(catalogo.map((v) => v.idioma).filter((x): x is string => !!x))).sort()], [catalogo]);
  // Filtro AND: una voz aparece sólo si pasa género Y idioma ('Todas' = no filtrar).
  const filtradas = catalogo.filter((v) => (genero === 'Todas' || v.sexo === genero) && (idioma === 'Todas' || v.idioma === idioma));

  const [sonando, setSonando] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const parar = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setSonando(null);
  };
  useEffect(() => {
    if (!abierto) parar();
    return parar;
  }, [abierto]);
  const escuchar = (v: VozDelEstudio) => {
    if (sonando === v.id || !v.muestraUrl) return parar();
    parar();
    const audio = new Audio(v.muestraUrl);
    audio.onended = () => setSonando(null);
    audio.onerror = () => setSonando(null);
    audioRef.current = audio;
    setSonando(v.id);
    void audio.play().catch(() => setSonando(null));
  };
  const elegir = (voz: VozDeVideo | null) => {
    parar();
    onElegir(voz);
    onCerrar();
  };

  const fila = (v: VozDelEstudio) => {
    const activa = elegida === v.id;
    const desc = descripcionLimpia(v.descripcion);
    return (
      <div
        key={v.id}
        className={`flex items-center gap-2 rounded-md p-3 transition-[background-color,box-shadow] ${
          activa ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)] ring-2 ring-brand-primary' : 'bg-white ring-1 ring-black/[0.06] hover:bg-black/[0.02]'
        }`}
      >
        <button type="button" role="radio" aria-checked={activa} onClick={() => elegir(v.id)} className="min-w-0 flex-1 text-left">
          <span className="flex items-center gap-2">
            <span className="truncate text-[14px] font-medium text-brand-black">{nombreCorto(v.nombre)}</span>
            {activa && <Check className="h-3.5 w-3.5 shrink-0 text-brand-primary" aria-hidden="true" />}
          </span>
          {(v.idioma || v.acento) && (
            <span className="mt-0.5 flex items-center gap-1.5 text-[12px] text-[#3C3C43]/40">
              {v.idioma && <span>{v.idioma}</span>}
              {v.idioma && v.acento && <span className="text-[#3C3C43]/25">·</span>}
              {v.acento && <span>{v.acento}</span>}
            </span>
          )}
          {(v.sexo || desc) && (
            <span className="mt-0.5 block truncate text-[12px] text-[#3C3C43]/55">
              {v.sexo && <span className="font-medium">{v.sexo}</span>}
              {v.sexo && desc && <span> · </span>}
              {desc}
            </span>
          )}
        </button>
        {v.muestraUrl && (
          <button
            type="button"
            onClick={() => escuchar(v)}
            aria-label={sonando === v.id ? `Parar la muestra de ${nombreCorto(v.nombre)}` : `Escuchar a ${nombreCorto(v.nombre)}`}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#EEEFF3] text-brand-black transition-colors hover:bg-[#E2E4EA]"
          >
            {sonando === v.id ? <Pause className="h-3.5 w-3.5" /> : <Play className="ml-0.5 h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    );
  };

  const opcionFija = (id: 'auto' | null, Icono: typeof Mic, titulo: string, bajada: string) => {
    const activa = elegida === id;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={activa}
        onClick={() => elegir(id)}
        className={`flex w-full items-center gap-3 rounded-md p-3 text-left transition-[background-color,box-shadow] ${
          activa ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)] ring-2 ring-brand-primary' : 'bg-white ring-1 ring-black/[0.06] hover:bg-black/[0.02]'
        }`}
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-sm bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary">
          <Icono className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-[14px] font-medium text-brand-black">
            {titulo}
            {activa && <Check className="h-3.5 w-3.5 shrink-0 text-brand-primary" aria-hidden="true" />}
          </span>
          <span className="block text-[12px] leading-snug text-[#3C3C43]/55">{bajada}</span>
        </span>
      </button>
    );
  };

  return (
    <HojaLateral abierta={abierto} titulo="Seleccionar voz" bajada={`Para ${para}`} onCerrar={onCerrar}>
      <div className="flex flex-col gap-2 px-1 pb-2" role="radiogroup" aria-label="Voz">
        {opcionFija('auto', Sparkles, 'Automática', bajadaDeAutomatica ?? 'Atlas elige por producto: voz de mujer o de hombre según para quién es')}
        {opcionFija(null, VolumeX, 'Sin voz', 'Sólo la música de fondo, sin locución ni subtítulos')}
        <div className="mt-2 flex gap-2">
          {(['mis-voces', 'voces-atlas'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPestana(p)}
              className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${pestana === p ? 'bg-brand-primary text-white' : 'bg-black/[0.05] text-[rgb(var(--brand-black-rgb)_/_0.6)] hover:bg-black/[0.08]'}`}
            >
              {p === 'mis-voces' ? `Mis voces${clonadas.length ? ` (${clonadas.length})` : ''}` : 'Voces Atlas'}
            </button>
          ))}
        </div>
        {cargando ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-brand-primary" aria-hidden="true" />
          </div>
        ) : pestana === 'mis-voces' ? (
          clonadas.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <Mic className="h-10 w-10 text-[rgb(var(--brand-black-rgb)_/_0.3)]" aria-hidden="true" />
              <p className="text-[14px] text-[#3C3C43]/60">Todavía no clonaste ninguna voz. Se clonan desde el Estudio de Video.</p>
            </div>
          ) : (
            clonadas.map(fila)
          )
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <SelectorDeOpciones rotulo="Género" valor={genero} opciones={generos.map((g) => ({ id: g, etiqueta: g }))} elegido={genero} onElegir={setGenero} />
              <SelectorDeOpciones rotulo="Idioma" valor={idioma === 'Todas' ? 'Todos' : idioma} opciones={idiomas.map((i) => ({ id: i, etiqueta: i === 'Todas' ? 'Todos' : i }))} elegido={idioma} onElegir={setIdioma} />
            </div>
            {filtradas.length === 0 ? <p className="py-12 text-center text-[13px] text-[#3C3C43]/50">No se encontraron voces.</p> : filtradas.map(fila)}
          </>
        )}
      </div>
    </HojaLateral>
  );
};
